import React from 'react';
import {BotFormSelectionProvider, BotRequest, BotRequestPartial} from "../BotForm";
import styles from "../BotForm.module.css";
import {CustomInputField} from "../../../../../elements/customInputField/CustomInputField";
import {ValidationResult} from "../../../../../elements/validator/component/ValidationComponent";
import {
    DefaultValidValidator,
    IntegerNumberValidator,
    NotEmptyStringValidator,
    OptionalPositiveNumberValidator,
    UrlValidator
} from "../../../../../elements/validator/Validator";
import {Section} from "../../../../../elements/section/Section";
import {ValidatedInputField} from "../../../../../elements/validator/component/ValidatedInputField";
import {ValidatedTextAreaField} from "../../../../../elements/validator/component/ValidatedTextAreaField";
import {useAppSelector} from "../../../../../../app/hooks";
import {CustomInputDateField} from "../../../../../elements/customInputDateField/CustomInputDateField";
import {LocaleTag, RedirectProvider, SupportedTimezone} from "../../../../../../api/generated";
import {allRedirectProviderOptions} from "./util/redirectProviderUtil";
import {ValidatedSelectField} from "../../../../../elements/validator/component/ValidatedSelectField";
import {SelectOption} from "../../../../../elements/select/SelectDropdown";
import {
    allDefaultLocaleOptions,
    allSupportedTimezoneOptions,
    getLocalePolicyPreview,
    getSupportedTimezoneLabel,
    toIntlLocaleTag
} from "./util/localizationPolicyUtil";
import {FranchiseInformation} from "./FranchiseInformation";
import {FranchiseRequestUi} from "../../../franchiseTypes";
import {getLocaleTagTitle} from "../../../../../../utils/enumValueTitleUtil";
import {
    CustomSwitchFieldWithTitle
} from "../../../../../elements/customSwitchFieldWithTitle/CustomSwitchFieldWithTitle";

interface Props<T> {
    getMessage: (s: any) => any;
    updateRequest: (update: BotRequestPartial) => void;
    franchiseOptions: SelectOption<FranchiseRequestUi>[]
    selectionProvider: BotFormSelectionProvider;
    updateValidationStatus: (status: ValidationResult<T>) => void;
}

export const DealerInformation = <T extends BotRequest>({
                                                            getMessage,
                                                            updateRequest,
                                                            franchiseOptions,
                                                            selectionProvider,
                                                            updateValidationStatus,
                                                        }: Props<T>) => {

    const publicId = useAppSelector(selectionProvider.publicId)
    const locale = useAppSelector(selectionProvider.defaultLocale) ?? LocaleTag.EnGb;
    const localePolicy = getLocalePolicyPreview(locale);
    const timezone = useAppSelector(selectionProvider.timezone) ?? localePolicy.defaultTimezone;

    const previewTimestamp = new Intl.DateTimeFormat(toIntlLocaleTag(locale), {
        dateStyle: "medium",
        timeStyle: "short",
        timeZone: getSupportedTimezoneLabel(timezone)
    }).format(new Date());
    const previewDirection = localePolicy.direction;

    return <Section title={"Dealer Information"}>
        <div className={styles.fieldsLine}>
            <CustomInputField
                readOnly={true}
                isRequired={true}
                title={"Public Bot ID"}
                value={publicId}
                placeholder={"Aime's Cars"}
                setValue={() => 123}
            />
            <FranchiseInformation<T>
                getMessage={getMessage}
                updateRequest={updateRequest}
                franchiseOptions={franchiseOptions}
                selectionProvider={selectionProvider}
                updateValidationStatus={updateValidationStatus}
            />
        </div>
        <div className={styles.fieldsLine}>
            <ValidatedInputField<string, T>
                onChange={(v) => updateRequest({siteName: v})}
                updateValidationStatus={updateValidationStatus}
                fieldName={'siteName'}
                selector={selectionProvider.siteName}
                validator={NotEmptyStringValidator}
                label={'Site Name'}
                placeholder={"Aime's Cars"}
                getMessage={getMessage}
            />
            <ValidatedInputField<string, T>
                onChange={(v) => updateRequest({siteURL: v})}
                updateValidationStatus={updateValidationStatus}
                fieldName={'siteURL'}
                selector={selectionProvider.siteURL}
                validator={UrlValidator}
                label={'Site URL'}
                placeholder={"https://example.com"}
                getMessage={getMessage}
            />
        </div>
        <div className={styles.fieldsLineWithMargin}>
            <ValidatedInputField<string, T>
                required={false}
                onChange={(v) => updateRequest({groupHash: v})}
                updateValidationStatus={updateValidationStatus}
                fieldName={'groupHash'}
                selector={selectionProvider.groupHash}
                validator={DefaultValidValidator}
                label={'Group Hash'}
                placeholder={"Optional"}
                getMessage={getMessage}
            />
        </div>
        <CustomSwitchFieldWithTitle
            textOnTrue={"On"}
            textOnFalse={"Off"}
            title={"Isolated Mode"}
            description={"Skip customer-facing emails and third-party enquiry integrations for this assistant"}
            valueSelector={selectionProvider.externalCommunicationsDisabled}
            toggleValue={(v) => updateRequest({externalCommunicationsDisabled: v})}
        />
        <div className={styles.fieldsLineWithMargin}>
            <ValidatedInputField<number, T>
                required={false}
                onChange={(v) => updateRequest({workingRange: v})}
                updateValidationStatus={updateValidationStatus}
                fieldName={'workingRange'}
                selector={selectionProvider.workingRange}
                validator={OptionalPositiveNumberValidator}
                label={'Working Range'}
                placeholder={"e.g., 80"}
                getMessage={getMessage}
            />
            <ValidatedInputField<number, T>
                onChange={(v) => updateRequest({conversationLimit: v})}
                updateValidationStatus={updateValidationStatus}
                fieldName={'conversationLimit'}
                selector={selectionProvider.conversationLimit}
                validator={IntegerNumberValidator}
                label={'Conversation Limit'}
                placeholder={"e.g., 500"}
                getMessage={getMessage}
            />
        </div>
        <div className={styles.fieldsLineWithMargin}>
            <ValidatedSelectField<RedirectProvider, T>
                onChange={(v) => updateRequest({redirectProvider: v})}
                updateValidationStatus={updateValidationStatus}
                fieldName={'redirectProvider'}
                selector={selectionProvider.redirectProvider}
                validator={NotEmptyStringValidator}
                label={'Redirect Pattern'}
                getMessage={getMessage}
                options={allRedirectProviderOptions}
            />
            <CustomInputDateField
                title={"Last Renewal Date"}
                valueSelector={selectionProvider.lastRenewalDate}
                updateValue={(v) => updateRequest({lastRenewalDate: v})}
            />
        </div>
        <div className={styles.fieldsLineWithMargin}>
            <ValidatedSelectField<LocaleTag, T>
                onChange={(v) => updateRequest({defaultLocale: v})}
                updateValidationStatus={updateValidationStatus}
                fieldName={'defaultLocale'}
                selector={selectionProvider.defaultLocale}
                validator={NotEmptyStringValidator}
                label={'Default Locale'}
                getMessage={getMessage}
                options={allDefaultLocaleOptions}
            />
            <ValidatedSelectField<SupportedTimezone, T>
                onChange={(v) => updateRequest({timezone: v})}
                updateValidationStatus={updateValidationStatus}
                fieldName={'timezone'}
                selector={selectionProvider.timezone}
                validator={NotEmptyStringValidator}
                label={'Timezone'}
                getMessage={getMessage}
                options={allSupportedTimezoneOptions}
            />
        </div>
        <div className={styles.fieldsLineWithMargin}>
            <div className={styles.localizationPreview} dir={previewDirection === "RTL" ? "rtl" : "ltr"}>
                <div className={styles.localizationPreviewTitle}>Widget Preview</div>
                <div className={styles.localizationPreviewRow}>Locale: {getLocaleTagTitle(locale)}</div>
                <div className={styles.localizationPreviewRow}>Timezone: {getSupportedTimezoneLabel(timezone)}</div>
                <div className={styles.localizationPreviewRow}>Time: {previewTimestamp}</div>
                <div className={styles.localizationPreviewRow}>Direction: {previewDirection}</div>
            </div>
        </div>
        <ValidatedTextAreaField<string, T>
            required={false}
            onChange={(v) => updateRequest({teamInfo: v})}
            updateValidationStatus={updateValidationStatus}
            fieldName={'teamInfo'}
            selector={selectionProvider.teamInfo}
            validator={DefaultValidValidator}
            label={'Team Information'}
            getMessage={getMessage}
            maxLength={40000}
            placeholder={"Information about dealer team"}
        />
    </Section>
}
